import { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Building2, Heart, House, LayoutDashboard, Loader2, LogOut, MessageSquare, Plus, UserRound, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/site/common";
import { TYPES, memberApi, useMember } from "@/site/member";
import { MemberAvatar, TrustBadges } from "@/site/Badges";
import { AccountCtx } from "@/site/account/ctx";
import MyListings from "@/site/account/MyListings";
import Profile from "@/site/account/Profile";
import Overview from "@/site/account/Overview";
import Messages from "@/site/account/Messages";
import Favorites from "@/site/account/Favorites";
import Agents from "@/site/account/Agents";

export function RequireMember({ children, complete }) {
  const { member } = useMember(), { pathname } = useLocation();
  if (member === undefined) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-orange-500" /></div>;
  if (!member) return <Navigate to={`/giris?next=${encodeURIComponent(pathname)}`} replace />;
  if (complete && !member.complete) return <Navigate to="/hesabim/profil" replace />;
  return children;
}

const navItems = (corp) => [["", "Özet", LayoutDashboard, "ozet"], ["ilanlar", "İlanlarım", House, "ilanlarim"], ["mesajlar", "Mesajlarım", MessageSquare, "mesajlarim"],
  ["favoriler", "Favorilerim", Heart, "favorilerim"], ...(corp ? [["danismanlar", "Danışmanlarım", Users, "danismanlarim"]] : []), ["profil", corp ? "Ofisim" : "Profilim", corp ? Building2 : UserRound, "ofisim"]];

function NavItems({ corp, unread, mobile }) {
  const cls = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap border transition-colors ${isActive ? "bg-orange-50 text-orange-600 border-orange-200/70" : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`;
  return navItems(corp).map(([to, label, Icon, id]) => (
    <NavLink key={id} end={to === ""} to={`/hesabim${to ? `/${to}` : ""}`} className={cls} data-testid={`${mobile ? "mnav" : "nav"}-tab-${id}`}>
      <Icon size={17} /><span className="flex-1">{label}</span>{id === "mesajlarim" && unread > 0 && <span className="min-w-[22px] rounded-full bg-orange-500 px-1.5 py-0.5 text-center text-[11px] font-bold text-white" data-testid={`${mobile ? "mnav" : "nav"}-unread-badge`}>{unread}</span>}
    </NavLink>
  ));
}

function Sidebar({ member, unread, logout }) {
  const corp = member.type === "kurumsal";
  return (
    <aside className="hidden lg:flex w-72 shrink-0 flex-col gap-5 sticky top-24 self-start" data-testid="member-sidebar">
      <div className="rounded-3xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-3.5"><MemberAvatar m={member} size="w-14 h-14" testid="account-avatar" />
          <div className="min-w-0"><p className="font-extrabold text-slate-900 leading-tight truncate" data-testid="account-name">{corp && member.company ? member.company : member.name || member.email}</p>
            {member.type && <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700" data-testid="account-type-badge">{corp ? <Building2 size={12} /> : <UserRound size={12} />}{TYPES[member.type]} üye</span>}</div></div>
        <div className="mt-3"><TrustBadges badges={member.badges} compact testid="account-badges" /></div>
        <Link to="/ilan-ver" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 transition-colors" data-testid="account-post-btn"><Plus size={18} />Ücretsiz İlan Ver</Link>
      </div>
      <nav className="rounded-3xl border border-slate-200 bg-white p-2.5 flex flex-col gap-1"><NavItems corp={corp} unread={unread} />
        <button onClick={logout} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid="account-logout-btn"><LogOut size={17} />Çıkış yap</button></nav>
    </aside>
  );
}

function MobileHead({ member, unread }) {
  const corp = member.type === "kurumsal";
  return (
    <div className="lg:hidden space-y-4" data-testid="member-mobile-head">
      <div className="flex items-center gap-3"><MemberAvatar m={member} size="w-12 h-12" /><div className="min-w-0 flex-1"><p className="font-extrabold text-slate-900 truncate">{corp && member.company ? member.company : member.name || member.email}</p><p className="text-xs text-slate-500">{TYPES[member.type] || ""} üye</p></div>
        <Link to="/ilan-ver" className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-white text-sm font-bold px-4 py-2.5" data-testid="account-post-btn-mobile"><Plus size={16} />İlan Ver</Link></div>
      <nav className="-mx-4 px-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]"><NavItems corp={corp} unread={unread} mobile /></nav>
    </div>
  );
}

export default function Account() {
  const { member, logout } = useMember(), { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  const refreshUnread = useCallback(() => { memberApi.get(`${API}/member/unread`).then((r) => setUnread(r.data.unread)).catch(() => {}); }, []);
  useEffect(() => { if (member) refreshUnread(); }, [member, pathname, refreshUnread]);
  return (
    <RequireMember>
      {member && <AccountCtx.Provider value={{ unread, refreshUnread }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 flex flex-col lg:flex-row gap-8" data-testid="site-account-page">
          <Sidebar member={member} unread={unread} logout={logout} />
          <MobileHead member={member} unread={unread} />
          <main className="flex-1 min-w-0">
            <Routes>
              <Route index element={<Overview />} />
              <Route path="ilanlar" element={<MyListings />} />
              <Route path="mesajlar" element={<Messages />} />
              <Route path="favoriler" element={<Favorites />} />
              <Route path="danismanlar" element={member.type === "kurumsal" ? <Agents /> : <Navigate to="/hesabim" replace />} />
              <Route path="profil" element={<Profile />} />
              <Route path="*" element={<Navigate to="/hesabim" replace />} />
            </Routes>
          </main>
        </div>
      </AccountCtx.Provider>}
    </RequireMember>
  );
}
